import { BrowserProvider, type JsonRpcProvider, type Eip1193Provider, getBytes, hexlify } from 'ethers'
import {
  wrapEthereumProvider,
  type EIP2696_EthereumProvider,
  KeyFetcher,
} from '@oasisprotocol/sapphire-paratime'
 
// @ts-ignore — cborg exports map lacks a "types" entry; types are declared in definitions.d.ts
import { encode as cborEncode, decode as cborDecode } from 'cborg'

// ---------------------------------------------------------------------------
// Unsigned / encrypted simulation (msg.sender = 0x0 on Oasis — limited use)
// ---------------------------------------------------------------------------

export const createSapphireSimulationProvider = (readOnlyProvider: JsonRpcProvider): BrowserProvider => {
  const minimalEip1193: EIP2696_EthereumProvider = {
    request: ({ method, params = [] }: { method: string; params?: unknown }) =>
      readOnlyProvider.send(method, (params as unknown[]) ?? []),
  }

  const wrapped = wrapEthereumProvider(minimalEip1193)
  return new BrowserProvider(wrapped as unknown as Eip1193Provider)
}

export const isSapphireEnvelopeFailure = (error: unknown): boolean => {
  if (!error || typeof error !== 'object') return false

  // EnvelopeError thrown by cipher.decryptResult when the envelope contains
  // a `fail` field: { message: "…", response: { module, code } }
  const { response } = error as { response?: unknown }
  if (response && typeof response === 'object' && 'module' in response && 'code' in response) {
    return true
  }

  const { code } = error as { code?: string }
  return code === 'CALL_EXCEPTION'
}

// ---------------------------------------------------------------------------
// Signed eth_call — sets msg.sender = wallet.address on Oasis
//
// Oasis Sapphire supports EIP-712 signed queries that cryptographically bind
// a call to a sender address, a specific block range (leash), and the
// encrypted calldata. The node derives msg.sender from the signature, so Safe's
// pre-validated signatures (v=1, r=owner) work correctly.
//
// Reference: sapphire-paratime Python SDK — sapphirepy/sapphire.py
//            `_new_signed_call_data_pack` and `_encrypt_tx_params`
//
// ⚠️  Browser UX: eth_signTypedData_v4 pops a MetaMask confirmation.
//     Use signedCallCache to limit confirmations to one per leash lifetime
//     (~90 s on Oasis mainnet, 15 × 6 s blocks).
// ---------------------------------------------------------------------------

const SIGNED_QUERY_DOMAIN_NAME = 'oasis-runtime-sdk/evm: signed query'
const SIGNED_QUERY_DOMAIN_VERSION = '1.0.0'
const DEFAULT_GAS_PRICE = 100_000_000_000n
const DEFAULT_GAS_LIMIT = 30_000_000n
const DEFAULT_BLOCK_RANGE = 15 // number of blocks the leash stays valid

const CALL_EIP712_TYPES = {
  Call: [
    { name: 'from', type: 'address' },
    { name: 'to', type: 'address' },
    { name: 'gasLimit', type: 'uint64' },
    { name: 'gasPrice', type: 'uint256' },
    { name: 'value', type: 'uint256' },
    { name: 'data', type: 'bytes' },
    { name: 'leash', type: 'Leash' },
  ],
  Leash: [
    { name: 'nonce', type: 'uint64' },
    { name: 'blockNumber', type: 'uint64' },
    { name: 'blockHash', type: 'bytes32' },
    { name: 'blockRange', type: 'uint64' },
  ],
}

export type SignedCallParams = {
  from: string
  to: string
  data: string
  gasLimit?: bigint
  value?: bigint
}

// Cache entry: keep the signed call hex string until the leash expires
type SignedCallCache = {
  data: string // original calldata that was signed
  leashBlock: number // block_number - 1 used in the leash
  signedData: string // hex-encoded CBOR data pack to send as eth_call data
  cipher: ReturnType<InstanceType<typeof KeyFetcher>['cipherSync']>
}

// Module-level cache: keyed by `from:to:data` — one per unique call shape
const _cache = new Map<string, SignedCallCache>()

// In-flight deduplication: prevents concurrent calls from triggering multiple MetaMask popups.
// If buildOasisSignedCall is called twice before the first resolves (React re-render race),
// the second caller awaits the same promise instead of starting a duplicate signing flow.
const _inFlight = new Map<string, Promise<{ signedData: string; cipher: ReturnType<InstanceType<typeof KeyFetcher>['cipherSync']> }>>()

/**
 * Builds the signed-call hex payload for `eth_call` on Oasis Sapphire.
 *
 * The result is meant to replace the `data` field in an `eth_call` request.
 * The call is encrypted with an ephemeral X25519 key and authenticated via an
 * EIP-712 signature so that Oasis sets `msg.sender = params.from` inside the
 * EVM.
 *
 * MetaMask will show a sign confirmation **once** per leash lifetime (~90 s).
 * After that, the cached payload is reused until the leash block falls outside
 * the valid range, at which point a fresh signature is requested.
 */
export const buildOasisSignedCall = async (
  readOnlyProvider: JsonRpcProvider,
  walletProvider: BrowserProvider,
  params: SignedCallParams,
  chainId: number,
): Promise<{ signedData: string; cipher: ReturnType<InstanceType<typeof KeyFetcher>['cipherSync']> }> => {
  const cacheKey = `${params.from}:${params.to}:${params.data}`
  const currentBlock = await readOnlyProvider.getBlockNumber()

  // Return cached entry if leash is still valid
  const cached = _cache.get(cacheKey)
  if (cached && currentBlock <= cached.leashBlock + DEFAULT_BLOCK_RANGE - 1) {
    return { signedData: cached.signedData, cipher: cached.cipher }
  }

  // Return in-flight promise if a concurrent call is already building the same signed call.
  // Without this, two simultaneous renders would both miss the cache, both prompt MetaMask,
  // and produce different ciphertexts for the same logical call.
  const inflight = _inFlight.get(cacheKey)
  if (inflight) return inflight

  const buildPromise = (async () => {
    try {
      // 1. Fetch calldata public key and create cipher
      const fetcher = new KeyFetcher()
      const minimalEip1193: EIP2696_EthereumProvider = {
        request: ({ method, params: p = [] }: { method: string; params?: unknown }) =>
          readOnlyProvider.send(method, (p as unknown[]) ?? []),
      }
      await fetcher.fetch(minimalEip1193)
      const cipher = fetcher.cipherSync()

      // 2. Encrypt calldata — returns CBOR-encoded envelope bytes
      const encryptedCalldata = cipher.encryptCall(params.data)
      // Decode back to dict so it embeds correctly inside the outer CBOR data pack
      const encryptedEnvelopeDict = cborDecode(getBytes(encryptedCalldata))

      // 3. Build leash (replay-protection window anchored to block N-1)
      const leashBlockNumber = currentBlock - 1
      const leashBlock = await readOnlyProvider.getBlock(leashBlockNumber)
      if (!leashBlock?.hash) throw new Error('Could not fetch leash block')

      const senderNonce = await readOnlyProvider.getTransactionCount(params.from)

      // 4. EIP-712 domain + message (signs over original unencrypted calldata)
      const domain = {
        name: SIGNED_QUERY_DOMAIN_NAME,
        version: SIGNED_QUERY_DOMAIN_VERSION,
        chainId,
      }

      const message = {
        from: params.from,
        to: params.to,
        gasLimit: params.gasLimit ?? DEFAULT_GAS_LIMIT,
        gasPrice: DEFAULT_GAS_PRICE,
        value: params.value ?? 0n,
        data: getBytes(params.data), // sign the original bytes, not the encrypted form
        leash: {
          nonce: BigInt(senderNonce),
          blockNumber: BigInt(leashBlockNumber),
          blockHash: leashBlock.hash as `0x${string}`,
          blockRange: BigInt(DEFAULT_BLOCK_RANGE),
        },
      }

      // 5. Sign — shows MetaMask popup; cached for DEFAULT_BLOCK_RANGE blocks afterward
      const signer = await walletProvider.getSigner()
      const signature = await signer.signTypedData(domain, CALL_EIP712_TYPES, message)

      // 6. CBOR-encode the signed data pack
      //    Structure mirrors the Python SDK's _new_signed_call_data_pack:
      //    { data: <encrypted_envelope_dict>, leash: { nonce, block_number, block_hash, block_range }, signature: <bytes> }
      const dataPack = {
        data: encryptedEnvelopeDict,
        leash: {
          nonce: senderNonce,
          block_number: leashBlockNumber,
          block_hash: getBytes(leashBlock.hash),
          block_range: DEFAULT_BLOCK_RANGE,
        },
        signature: getBytes(signature),
      }

      const signedData = hexlify(cborEncode(dataPack))

      _cache.set(cacheKey, { data: params.data, leashBlock: leashBlockNumber, signedData, cipher })

      return { signedData, cipher }
    } finally {
      _inFlight.delete(cacheKey)
    }
  })()

  _inFlight.set(cacheKey, buildPromise)
  return buildPromise
}

// ethers v6 wraps JSON-RPC errors; the raw RPC code lives at error.error?.code or error.info?.error?.code
const isJsonRpcRevert = (error: unknown): boolean => {
  if (!error || typeof error !== 'object') return false
  const rpcCode =
    (error as { error?: { code?: number } }).error?.code ??
    (error as { info?: { error?: { code?: number } } }).info?.error?.code
  return rpcCode === 3
}

/**
 * Runs a signed eth_call on Oasis Sapphire so that msg.sender = params.from
 * inside the Safe contract. This is the only reliable pre-execution validation
 * on Oasis — unsigned eth_call always has msg.sender = 0x0.
 *
 * Throws if the transaction would revert (Safe contract error propagated
 * through the decrypted envelope). Silent for success.
 *
 * ⚠️  Requires a MetaMask sign confirmation on first call per leash window.
 */
export const validateOasisSignedCall = async (
  readOnlyProvider: JsonRpcProvider,
  walletProvider: BrowserProvider,
  params: SignedCallParams,
  chainId: number,
): Promise<void> => {
  const { signedData, cipher } = await buildOasisSignedCall(readOnlyProvider, walletProvider, params, chainId)

  // gas and gasPrice MUST match the values that were signed in the EIP-712 message.
  // Oasis reads them from the eth_call params to reconstruct the message for signature verification.
  const gasLimit = params.gasLimit ?? DEFAULT_GAS_LIMIT
  const gasHex = `0x${gasLimit.toString(16)}`
  const gasPriceHex = `0x${DEFAULT_GAS_PRICE.toString(16)}`

  let rawResult: string
  try {
    rawResult = (await readOnlyProvider.send('eth_call', [
      { to: params.to, from: params.from, data: signedData, gas: gasHex, gasPrice: gasPriceHex },
      'latest',
    ])) as string
  } catch (error) {
    // Oasis returns a plain JSON-RPC code 3 error for execution reverts inside signed eth_calls
    // (rather than an encrypted envelope failure). Propagate these so the UI shows the warning.
    if (isJsonRpcRevert(error)) throw error
    // Other RPC errors (epoch expired, invalid leash, format issue) — validation unavailable.
    return
  }

  // cipher.decryptResult throws EnvelopeError with { fail: { module, code, message } } when the
  // Safe contract reverted inside the encrypted envelope. Only propagate genuine on-chain failures;
  // swallow format errors (e.g. "Received unencrypted envelope") which indicate unavailable validation.
  try {
    cipher.decryptResult(rawResult)
  } catch (error) {
    if (isSapphireEnvelopeFailure(error)) throw error
  }
}
